import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Users, 
  Star, 
  Instagram, 
  Send, 
  CheckCircle, 
  ExternalLink,
  Filter,
  TrendingUp
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import PartnerLayout from "./PartnerLayout";

interface Influencer {
  id: string;
  userId: string;
  displayName: string;
  bio: string | null;
  avatar?: string;
  followerCount: number;
  averageEngagement: string | null;
  cuisineSpecialties: string[] | null;
  acceptsKudozPayment: boolean;
  kudozRate: number | null;
  pricePerPost: string | null;
  isVerified: boolean;
  socialLinks?: {
    instagram?: string;
    tiktok?: string;
    youtube?: string;
  };
}

interface MyOffer {
  id: string;
  influencerId: string;
  influencerName: string;
  title: string;
  status: string;
  offerType: string;
  createdAt: string;
}

export default function PartnerInfluencers() {
  const { toast } = useToast();
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  const [cuisineFilter, setCuisineFilter] = useState<string>('all');
  const [selectedInfluencer, setSelectedInfluencer] = useState<Influencer | null>(null);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  
  const { data: influencers, isLoading: influencersLoading } = useQuery<Influencer[]>({
    queryKey: ['/api/partner/influencers', cuisineFilter],
    enabled: !!restaurantId,
  });
  
  const { data: myOffers, isLoading: offersLoading } = useQuery<MyOffer[]>({
    queryKey: ['/api/partner/influencer-offers', restaurantId],
    enabled: !!restaurantId,
  });
  
  const sendOfferMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest('POST', `/api/partner/influencer-offers`, data);
    },
    onSuccess: () => {
      toast({ title: "Offer sent", description: "Your collaboration offer has been sent to the influencer." });
      setShowOfferDialog(false);
      setSelectedInfluencer(null);
      queryClient.invalidateQueries({ queryKey: ['/api/partner/influencer-offers'] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to send offer. Please try again.", variant: "destructive" });
    },
  });
  
  const formatFollowers = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'declined': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-influencers-title">Influencers</h1>
            <p className="text-muted-foreground">Connect with local food content creators</p>
          </div>
          
          <Select value={cuisineFilter} onValueChange={setCuisineFilter}>
            <SelectTrigger className="w-[180px]" data-testid="select-cuisine-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by cuisine" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cuisines</SelectItem>
              <SelectItem value="italian">Italian</SelectItem>
              <SelectItem value="japanese">Japanese</SelectItem>
              <SelectItem value="mexican">Mexican</SelectItem>
              <SelectItem value="american">American</SelectItem>
              <SelectItem value="vegan">Vegan/Vegetarian</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Browse Influencers</CardTitle>
                <CardDescription>Find the perfect content creator for your restaurant</CardDescription>
              </CardHeader>
              <CardContent>
                {influencersLoading ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {[1, 2, 3, 4].map(i => (
                      <Skeleton key={i} className="h-48" />
                    ))}
                  </div>
                ) : influencers && influencers.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {influencers.map((influencer) => (
                      <div 
                        key={influencer.id} 
                        className="p-4 rounded-lg border hover-elevate cursor-pointer"
                        onClick={() => {
                          setSelectedInfluencer(influencer);
                          setShowOfferDialog(true);
                        }}
                        data-testid={`influencer-card-${influencer.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={influencer.avatar} />
                            <AvatarFallback>{influencer.displayName[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">{influencer.displayName}</p>
                              {influencer.isVerified && (
                                <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {influencer.bio || 'Food content creator'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>{formatFollowers(influencer.followerCount)}</span>
                          </div>
                          {influencer.averageEngagement && (
                            <div className="flex items-center gap-1">
                              <TrendingUp className="h-4 w-4 text-muted-foreground" />
                              <span>{parseFloat(influencer.averageEngagement).toFixed(1)}% eng</span>
                            </div>
                          )}
                        </div>
                        
                        {influencer.cuisineSpecialties && influencer.cuisineSpecialties.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {influencer.cuisineSpecialties.slice(0, 3).map(cuisine => (
                              <Badge key={cuisine} variant="outline" className="text-xs">
                                {cuisine}
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between mt-4 pt-3 border-t">
                          {influencer.acceptsKudozPayment && influencer.kudozRate ? (
                            <span className="text-sm font-medium text-primary">
                              {influencer.kudozRate.toLocaleString()} Kudoz
                            </span>
                          ) : influencer.pricePerPost ? (
                            <span className="text-sm font-medium">
                              ${parseFloat(influencer.pricePerPost).toFixed(0)}/post
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">Contact for rates</span>
                          )}
                          <Button size="sm" variant="outline">
                            <Send className="h-3 w-3 mr-1" />
                            Send Offer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium">No influencers found</p>
                    <p className="text-sm text-muted-foreground">
                      Try adjusting your filters to find content creators
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          <div>
            <Card>
              <CardHeader>
                <CardTitle>My Offers</CardTitle>
                <CardDescription>Track your collaboration requests</CardDescription>
              </CardHeader>
              <CardContent>
                {offersLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} className="h-16" />
                    ))}
                  </div>
                ) : myOffers && myOffers.length > 0 ? (
                  <div className="space-y-3">
                    {myOffers.map((offer) => (
                      <div 
                        key={offer.id} 
                        className="p-3 rounded-lg bg-muted/50"
                        data-testid={`my-offer-${offer.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-sm">{offer.title}</p>
                          <Badge className={getStatusColor(offer.status)} variant="outline">
                            {offer.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          To: {offer.influencerName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(offer.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8 text-sm">
                    No offers sent yet
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        
        <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Send Collaboration Offer</DialogTitle>
              <DialogDescription>
                Invite {selectedInfluencer?.displayName} to create content for your restaurant
              </DialogDescription>
            </DialogHeader>
            
            {selectedInfluencer && (
              <div className="space-y-4 pt-4">
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Avatar>
                    <AvatarImage src={selectedInfluencer.avatar} />
                    <AvatarFallback>{selectedInfluencer.displayName[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedInfluencer.displayName}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatFollowers(selectedInfluencer.followerCount)} followers
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Offer Title</Label>
                  <Input placeholder="e.g., Featured Dinner Experience" data-testid="input-offer-title" />
                </div>
                
                <div className="space-y-2">
                  <Label>Offer Type</Label>
                  <Select>
                    <SelectTrigger data-testid="select-offer-type">
                      <SelectValue placeholder="Select offer type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="meal_comp">Complimentary Meal</SelectItem>
                      <SelectItem value="kudoz_payment">Kudoz Payment</SelectItem>
                      <SelectItem value="cash_payment">Cash Payment</SelectItem>
                      <SelectItem value="hybrid">Hybrid (Meal + Payment)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Requirements</Label>
                  <Textarea 
                    placeholder="What would you like them to create? (e.g., 1 Instagram post, 2 stories)"
                    rows={3}
                    data-testid="textarea-requirements"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Deadline (optional)</Label>
                  <Input type="date" data-testid="input-deadline" />
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowOfferDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => sendOfferMutation.mutate({
                      influencerId: selectedInfluencer.id,
                      restaurantId,
                    })}
                    disabled={sendOfferMutation.isPending}
                    data-testid="button-send-offer"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    {sendOfferMutation.isPending ? 'Sending...' : 'Send Offer'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </PartnerLayout>
  );
}
